import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CallResource, CallTranscript, CallTranscriptSentence, CALL_DISPOSITIONS } from '../../../core/interfaces/communication.interface';
import { CallRecordsService } from '../../../core/services/call-records/call-records.service';

@Component({
  selector: 'app-call-detail',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './call-detail.component.html',
  styleUrl: './call-detail.component.css',
})
export class CallDetailComponent implements OnChanges, OnDestroy {
  @Input() call!: CallResource;
  @Output() close = new EventEmitter<void>();
  @ViewChild('audioPlayer') audioPlayer!: ElementRef<HTMLAudioElement>;

  // Recording
  recordingUrl: string | null = null;
  recordingLoading = false;
  recordingError = false;
  isPlaying = false;
  playbackTime = 0;
  playbackDuration = 0;

  // Transcript
  transcript: CallTranscript | null = null;
  transcriptLoading = false;
  transcriptPolling: any = null;
  activeSentenceIndex = -1;

  constructor(private callRecordsService: CallRecordsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['call'] && this.call) {
      this.resetState();
      this.loadTranscriptIfAvailable();
    }
  }

  ngOnDestroy(): void {
    this.stopTranscriptPolling();
  }

  private resetState(): void {
    this.recordingUrl = null;
    this.recordingLoading = false;
    this.recordingError = false;
    this.isPlaying = false;
    this.playbackTime = 0;
    this.playbackDuration = 0;
    this.transcript = null;
    this.transcriptLoading = false;
    this.activeSentenceIndex = -1;
    this.stopTranscriptPolling();
  }

  // === Recording ===

  playRecording(): void {
    if (this.recordingUrl && this.audioPlayer?.nativeElement) {
      // URL already loaded, toggle play/pause
      const audio = this.audioPlayer.nativeElement;
      if (this.isPlaying) {
        audio.pause();
      } else {
        audio.play();
      }
      return;
    }

    // Fetch fresh URL (temporal, 1h)
    this.recordingLoading = true;
    this.recordingError = false;
    console.log('[CallDetail] Fetching recording URL for:', this.call.reference);

    this.callRecordsService.getRecording(this.call.reference).subscribe({
      next: (res) => {
        this.recordingUrl = res.data.url;
        this.playbackDuration = res.data.duration || this.call.recording?.duration || 0;
        this.recordingLoading = false;
        console.log('[CallDetail] Recording URL received — duration:', this.playbackDuration, 's — format:', res.data.format);

        // Play after Angular renders the <audio> element
        setTimeout(() => {
          if (this.audioPlayer?.nativeElement) {
            this.audioPlayer.nativeElement.play();
          }
        }, 100);
      },
      error: (err) => {
        this.recordingLoading = false;
        this.recordingError = true;
        console.error('[CallDetail] Recording fetch failed:', err?.status, err?.error);
      },
    });
  }

  onAudioPlay(): void {
    this.isPlaying = true;
  }

  onAudioPause(): void {
    this.isPlaying = false;
  }

  onAudioEnded(): void {
    this.isPlaying = false;
    this.playbackTime = 0;
    this.activeSentenceIndex = -1;
  }

  onAudioTimeUpdate(): void {
    if (this.audioPlayer?.nativeElement) {
      this.playbackTime = this.audioPlayer.nativeElement.currentTime;
      this.syncTranscriptHighlight();
    }
  }

  onAudioError(): void {
    console.warn('[CallDetail] Audio playback error — URL may have expired, refetching...');
    this.recordingUrl = null;
    this.isPlaying = false;
    // URL expired (>1h), refetch
    this.playRecording();
  }

  seekTo(event: MouseEvent, barElement: HTMLElement): void {
    if (!this.audioPlayer?.nativeElement || !this.playbackDuration) return;
    const rect = barElement.getBoundingClientRect();
    const pct = (event.clientX - rect.left) / rect.width;
    this.audioPlayer.nativeElement.currentTime = pct * this.playbackDuration;
  }

  get progressPercent(): number {
    if (!this.playbackDuration) return 0;
    return (this.playbackTime / this.playbackDuration) * 100;
  }

  // === Transcript ===

  private loadTranscriptIfAvailable(): void {
    if (!this.call.transcript_status) return;

    if (this.call.transcript_status === 'completed') {
      this.fetchTranscript();
    } else if (this.call.transcript_status === 'queued') {
      this.fetchTranscript();
      this.startTranscriptPolling();
    }
    // 'failed', 'skipped' → no fetch
  }

  private fetchTranscript(): void {
    this.transcriptLoading = true;
    console.log('[CallDetail] Fetching transcript for:', this.call.reference);

    this.callRecordsService.getTranscript(this.call.reference).subscribe({
      next: (res) => {
        this.transcript = res.data;
        this.transcriptLoading = false;
        console.log('[CallDetail] Transcript received — status:', res.data.status, '— sentences:', res.data.sentences?.length || 0);

        if (res.data.status === 'completed') {
          this.stopTranscriptPolling();
        }
      },
      error: (err) => {
        this.transcriptLoading = false;
        console.warn('[CallDetail] Transcript not available:', err?.status);
      },
    });
  }

  private startTranscriptPolling(): void {
    this.stopTranscriptPolling();
    let attempts = 0;
    this.transcriptPolling = setInterval(() => {
      attempts++;
      if (attempts > 12) { // 12 * 10s = 2 min
        this.stopTranscriptPolling();
        console.log('[CallDetail] Transcript polling stopped after 2 min');
        return;
      }
      this.fetchTranscript();
    }, 10000);
  }

  private stopTranscriptPolling(): void {
    if (this.transcriptPolling) {
      clearInterval(this.transcriptPolling);
      this.transcriptPolling = null;
    }
  }

  refreshTranscript(): void {
    this.fetchTranscript();
  }

  private syncTranscriptHighlight(): void {
    if (!this.transcript?.sentences?.length) return;
    const currentMs = this.playbackTime * 1000;
    this.activeSentenceIndex = this.transcript.sentences.findIndex(
      (s, i, arr) => currentMs >= s.start_ms && (i === arr.length - 1 || currentMs < arr[i + 1].start_ms)
    );
  }

  seekToSentence(sentence: CallTranscriptSentence): void {
    if (this.audioPlayer?.nativeElement && this.recordingUrl) {
      this.audioPlayer.nativeElement.currentTime = sentence.start_ms / 1000;
      if (!this.isPlaying) {
        this.audioPlayer.nativeElement.play();
      }
    }
  }

  // === Utils ===

  formatDuration(seconds: number): string {
    if (seconds <= 0) return '0:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  formatMs(ms: number): string {
    return this.formatDuration(Math.floor(ms / 1000));
  }

  formatDateTime(dateStr: string | null): string {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      weekday: 'short', day: 'numeric', month: 'short',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  }

  getStatusClass(status: string): string {
    const map: Record<string, string> = {
      answered: 'bg-success', missed: 'bg-danger', busy: 'bg-warning',
      failed: 'bg-danger', initiated: 'bg-info', canceled: 'bg-secondary',
    };
    return map[status] || 'bg-secondary';
  }

  getDispositionClass(disposition: string | null): string {
    if (!disposition) return '';
    const map: Record<string, string> = {
      interested: 'badge-success-soft', deal_closed: 'badge-success-soft',
      callback: 'badge-info-soft', not_interested: 'badge-danger-soft',
      no_answer: 'badge-warning-soft', complaint: 'badge-danger-soft',
      follow_up: 'badge-info-soft', information_sent: 'badge-primary-soft',
    };
    return map[disposition] || 'badge-secondary-soft';
  }

  onClose(): void {
    this.close.emit();
  }
}
